/* eslint-disable camelcase */

import { pushedArray } from "../../utils/generalUtils";
import { ThomasEventTypes } from "./ThomasEventTypes";

/**
 * Polyfill for CustomEvent (https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill)
 */
(function polyfillCustomEvent() {
  if (typeof window === "undefined" || typeof window.CustomEvent === "function")
    return false;

  function CustomEvent(event, inputParams) {
    const params = inputParams || {
      bubbles: false,
      cancelable: false,
      detail: null,
    };
    const evt = document.createEvent("CustomEvent");
    evt.initCustomEvent(
      event,
      params.bubbles,
      params.cancelable,
      params.detail
    );
    return evt;
  }

  (window as any).CustomEvent = CustomEvent;
})();

/**
 * Property names and URL patterns to extract event context
 * properties from the location URL.
 */
const CONTEXT_URL_PATTERNS = {
  company_id: /cid=([0-9]{3,8})(?:[^0-9].*)?/,
  heading: /heading=([0-9]{3,8})(?:[^0-9].*)?/,
};

/**
 * Base class for events from the thomasnet.com React app. This
 * allows application components construct event objects which
 * are then emitted as CustomEvent objects from the enclosing
 * document.
 *
 * Note that the convention for event property names is
 * snake_case.
 */
export class ThomasEvent {
  /**
   * Emit a custom event to the document object in a specific
   * format. Note that a polyfill is required for IE9+ to support
   * window.CustomEvent -- this has been added to
   * `src/actions/thomas-events.js`.
   *
   * @param {string} componentName
   * @param {Object} detail
   */
  static emit(componentName, detail) {
    if (
      typeof window !== "undefined" &&
      typeof window.document !== "undefined"
    ) {
      const event = new CustomEvent(ThomasEventTypes.THOMAS_EVENT_NAME, {
        detail: {
          ...detail,
        },
      });

      /**
       * Standard dispatch event
       */
      window.document.dispatchEvent(event);

      /**
       * Queued Thomas Events
       * Events are additionally stored in this global object (up to MAX_QUEUED_THOMAS_EVENTS)
       * to enable dispatching at a later time.
       */
      const MAX_QUEUED_THOMAS_EVENTS = 1000;

      window.document.dispatchedThomasEvents = pushedArray(
        window.document.dispatchedThomasEvents,
        event,
        MAX_QUEUED_THOMAS_EVENTS
      );
    }
  }

  /**
   * Return an event context object for the current window. The
   * properties will be derived from the current location URL
   *
   * @return {Object} the event context
   */
  static getEventContext() {
    const context: { [key: string]: string } = {};

    if (typeof window !== "undefined") {
      if (typeof window.location !== "undefined") {
        context.url = window.location.href;

        if (context.url) {
          Object.entries(CONTEXT_URL_PATTERNS).forEach(([prop, pattern]) => {
            const groups = context.url.match(pattern);
            if (groups && groups.length > 0) {
              const [, match] = groups[1];
              context[prop] = match;
            }
          });
        }
      }
    }

    return context;
  }

  public actionType: string;

  public detail: any;

  public component: any;

  public context: any;

  public componentType?: ThomasEventTypes;

  /**
   * Create a new event instance.
   *
   * @param {string} actionType Identifier for the event.
   *                   Common action types are specified in
   *                   ThomasEventTypes, or subclasses can use
   *                   their own event types.
   * @param {Object} actionDetail Containing detailed event
   *                     information specific to the action type.
   * @param {Object} component Containing identifying information
   *                  about the UX component that is the source of
   *                  the event. This should contain a
   *                  componentType? property as defined in
   *                  ThomasEventTypes, and also a componentId.
   */
  constructor(actionType, actionDetail, component) {
    this.actionType = actionType;
    this.detail = actionDetail;
    this.component = component;
    this.context = ThomasEvent.getEventContext();
  }

  /**
   * Emit this event as a CustomEvent object through the enclosing
   * document event listener.
   */
  emit() {
    return ThomasEvent.emit(this.componentType, {
      action_type: this.actionType,
      ...this.context,
      ...this.component,
      ...this.detail,
    });
  }
}
